import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  LogOut,
  User,
  LayoutDashboard,
  Settings,
  Shield,
  Users,
  BarChart3,
  FileText,
  MessageSquare,
  Video,
  UserCircle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import './Navigation.css';

const Navigation = ({ onPageChange, currentPage }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const { user, userRole, signOut, switchRole } = useAuth();
  const navigate = useNavigate();

  const handleNavClick = (page) => {
    onPageChange(page);
    setMobileMenuOpen(false);
    setProfileDropdownOpen(false);
  };

  const handleRoleSwitch = (role) => {
    switchRole(role);
    setProfileDropdownOpen(false);
  };

  const handleSignOut = async () => {
    await signOut();
    setProfileDropdownOpen(false);
    navigate('/');
  };

  const clientMenuItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'appointments', label: 'Appointments', icon: BarChart3 },
    { id: 'billing', label: 'Billing', icon: FileText },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'messaging', label: 'Messages', icon: MessageSquare },
    { id: 'telehealth', label: 'Telehealth', icon: Video },
  ];

  const adminMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'clients', label: 'Manage Clients', icon: Users },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'docs', label: 'Documents', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const menuItems = userRole === 'admin' ? adminMenuItems : clientMenuItems;

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="nav-desktop">
        <div className="nav-container">
          {/* Brand */}
          <div className="nav-brand">
            <div className="nav-brand-icon">R</div>
            <div className="nav-brand-text">
              <div className="nav-brand-title">Rua Medical</div>
              <div className="nav-brand-subtitle">
                {userRole === 'admin' ? 'Admin Portal' : 'Medical Portal'}
              </div>
            </div>
          </div>

          {/* Desktop Menu Items */}
          <div className="nav-menu-desktop">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className={`nav-menu-item ${currentPage === item.id ? 'active' : ''}`}
                  onClick={() => handleNavClick(item.id)}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Desktop Right Section */}
          <div className="nav-right">
            {/* Role Switcher */}
            <div className="nav-role-switcher">
              <button
                className={`nav-role-btn ${userRole === 'client' ? 'active' : ''}`}
                onClick={() => handleRoleSwitch('client')}
              >
                <User size={16} />
              </button>
              <button
                className={`nav-role-btn ${userRole === 'admin' ? 'active' : ''}`}
                onClick={() => handleRoleSwitch('admin')}
              >
                <Shield size={16} />
              </button>
            </div>

            {/* Profile Dropdown */}
            <div className="nav-profile">
              <button
                className="nav-profile-btn"
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              >
                <UserCircle size={24} />
                <span>{user?.email?.split('@')[0] || 'User'}</span>
              </button>

              {profileDropdownOpen && (
                <div className="nav-dropdown">
                  <div className="nav-dropdown-header">
                    <p className="nav-dropdown-email">{user?.email}</p>
                    <p className="nav-dropdown-role">
                      {userRole === 'admin' ? 'Administrator' : 'Client'}
                    </p>
                  </div>
                  <div className="nav-dropdown-divider"></div>
                  <button
                    className="nav-dropdown-item"
                    onClick={() => handleNavClick('settings')}
                  >
                    <Settings size={16} />
                    <span>Account Settings</span>
                  </button>
                  <div className="nav-dropdown-divider"></div>
                  <button className="nav-dropdown-item danger" onClick={handleSignOut}>
                    <LogOut size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="nav-mobile">
        <div className="nav-mobile-header">
          {/* Brand */}
          <div className="nav-mobile-brand">
            <div className="nav-brand-icon">R</div>
            <div className="nav-brand-text">
              <div className="nav-brand-title">Rua</div>
            </div>
          </div>

          {/* Mobile Hamburger and Profile */}
          <div className="nav-mobile-actions">
            {/* Profile Icon */}
            <button
              className="nav-mobile-profile-btn"
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            >
              <UserCircle size={24} />
            </button>

            {/* Hamburger Menu */}
            <button
              className={`nav-mobile-menu-btn ${mobileMenuOpen ? 'open' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Profile Dropdown */}
          {profileDropdownOpen && (
            <div className="nav-mobile-dropdown">
              <div className="nav-dropdown-header">
                <p className="nav-dropdown-email">{user?.email}</p>
                <p className="nav-dropdown-role">
                  {userRole === 'admin' ? 'Administrator' : 'Client'}
                </p>
              </div>
              <div className="nav-dropdown-divider"></div>

              {/* Role Switcher Mobile */}
              <div className="nav-role-switcher-mobile">
                <button
                  className={`nav-role-btn-mobile ${userRole === 'client' ? 'active' : ''}`}
                  onClick={() => handleRoleSwitch('client')}
                >
                  <User size={16} />
                  <span>Client</span>
                </button>
                <button
                  className={`nav-role-btn-mobile ${userRole === 'admin' ? 'active' : ''}`}
                  onClick={() => handleRoleSwitch('admin')}
                >
                  <Shield size={16} />
                  <span>Admin</span>
                </button>
              </div>

              <div className="nav-dropdown-divider"></div>
              <button
                className="nav-dropdown-item"
                onClick={() => handleNavClick('settings')}
              >
                <Settings size={16} />
                <span>Account Settings</span>
              </button>
              <div className="nav-dropdown-divider"></div>
              <button className="nav-dropdown-item danger" onClick={handleSignOut}>
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="nav-mobile-menu">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className={`nav-mobile-menu-item ${
                    currentPage === item.id ? 'active' : ''
                  }`}
                  onClick={() => handleNavClick(item.id)}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </nav>
    </>
  );
};

export default Navigation;
